import { useEffect, useRef, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { createPortal } from "react-dom";
import { Menu, X, ChevronDown, Phone, Globe } from "lucide-react";
import { AGENCY } from "@/lib/api";
import { useLang } from "@/i18n/LanguageContext";

const LANGS = ["it", "en", "es"];
const LOGO_ASPECT = 1093 / 186;

const LangSwitch = () => {
  const { lang, changeLang } = useLang();
  return (
    <div className="flex items-center gap-1" data-testid="language-switcher">
      <Globe size={14} className="text-[#E8E8E8]" />
      {LANGS.map((l) => (
        <button
          key={l}
          data-testid={`lang-${l}`}
          onClick={() => changeLang(l)}
          className={`px-1.5 py-1 text-[11px] font-semibold uppercase tracking-widest transition-colors ${
            lang === l ? "text-white border-b border-[#E8E8E8]" : "text-[#8A94A0] hover:text-white"
          }`}
        >
          {l}
        </button>
      ))}
    </div>
  );
};

export const SiteNav = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [drop, setDrop] = useState(null);
  const [dropPos, setDropPos] = useState({ top: 0, left: 0 });
  const closeTimer = useRef(null);
  const [viewport, setViewport] = useState({ w: 1280, h: 800 });
  const [anchorTop, setAnchorTop] = useState(null);
  const [scrollPos, setScrollPos] = useState(0);
  const location = useLocation();
  const { t } = useLang();
  const isHome = location.pathname === "/";

  useEffect(() => {
    let ticking = false;
    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        const y = window.scrollY;
        setScrollPos(y);
        setScrolled(y > 24);
        ticking = false;
      });
    };
    const onResize = () => setViewport({ w: window.innerWidth, h: window.innerHeight });
    onResize();
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onResize);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onResize);
    };
  }, []);

  useEffect(() => {
    setOpen(false);
    setDrop(null);
  }, [location.pathname, location.search]);

  const openDrop = (key) => (e) => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    const r = e.currentTarget.getBoundingClientRect();
    setDropPos({ top: r.bottom, left: r.left + r.width / 2 });
    setDrop(key);
  };
  const closeDrop = () => {
    closeTimer.current = setTimeout(() => setDrop(null), 150);
  };

  useEffect(() => {
    if (!isHome) return;
    // Regola invalicabile: il punto di riposo del logo non può MAI scendere
    // sotto la posizione iniziale. Le ri-misurazioni possono solo alzarlo,
    // tranne quando cambia davvero la larghezza viewport (rotazione/resize reale).
    const measure = (force) => {
      const form = document.querySelector('[data-testid="property-search-form"]');
      const nav = document.querySelector('[data-testid="site-nav"]');
      if (form && nav) {
        const sy = window.scrollY;
        const next = {
          formTop: form.getBoundingClientRect().top + sy,
          // Il nav è position:fixed: la sua posizione NON va compensata con lo scroll,
          // altrimenti misurando durante lo scroll navBottom risulta gonfiato
          navBottom: nav.getBoundingClientRect().bottom,
        };
        setAnchorTop((prev) => (!prev || force || next.formTop < prev.formTop ? next : prev));
      }
    };
    measure(true);
    const id1 = setTimeout(() => measure(true), 400);
    const id2 = setTimeout(() => measure(true), 1200);
    const onResize = () => measure(false);
    window.addEventListener("resize", onResize);
    return () => {
      clearTimeout(id1);
      clearTimeout(id2);
      window.removeEventListener("resize", onResize);
    };
  }, [isHome, viewport.w]);

  useEffect(() => {
    if (!isHome || scrollPos !== 0) return;
    const form = document.querySelector('[data-testid="property-search-form"]');
    const nav = document.querySelector('[data-testid="site-nav"]');
    if (form && nav) {
      const next = { formTop: form.getBoundingClientRect().top, navBottom: nav.getBoundingClientRect().bottom };
      setAnchorTop((prev) => {
        if (!prev) return next;
        // Mai abbassare il punto di riposo: si accetta solo una misura che lo alza
        if (next.formTop >= prev.formTop) return prev;
        return Math.abs(prev.formTop - next.formTop) < 2 && Math.abs(prev.navBottom - next.navBottom) < 2 ? prev : next;
      });
    }
  }, [scrollPos, isHome]);

  const isDesktop = viewport.w >= 640;
  const smallH = isDesktop ? 44 : 36;
  const smallW = smallH * LOGO_ASPECT;
  const bigW = Math.min(viewport.w * 0.82, isDesktop ? 680 : 340);
  const bigH = bigW / LOGO_ASPECT;
  const bigTop = anchorTop
    ? Math.max(anchorTop.navBottom + (anchorTop.formTop - anchorTop.navBottom) / 2 - bigH / 2, anchorTop.navBottom + 8)
    : Math.max(viewport.h * 0.32 - bigH / 2, 90);
  const smallTop = 14;

  const progress = Math.min(Math.max(scrollPos / 320, 0), 1);
  const eased = 1 - Math.pow(1 - progress, 3);
  const logoBig = isHome && !open;
  const logoW = logoBig ? bigW + (smallW - bigW) * eased : smallW;
  // Blocco finale: la posizione verticale non supera MAI il punto di partenza (bigTop)
  const logoTop = logoBig ? Math.min(bigTop + (smallTop - bigTop) * eased, bigTop) : smallTop;

  const immobiliMenu = [
    { to: "/immobili?contract=vendita", label: t("nav.vendita") },
    { to: "/immobili?contract=affitto", label: t("nav.affitto") },
    { to: "/immobili?category=prestigio", label: t("nav.prestigio") },
    { to: "/immobili?category=commerciali", label: t("nav.commerciali") },
    { to: "/immobili?category=cantieri", label: t("nav.cantieri") },
  ];

  const agenziaMenu = [
    { key: "chi-siamo", to: "/chi-siamo", label: t("nav.chiSiamo") },
    { key: "blog", to: "/blog", label: t("nav.blog") },
    { key: "faq", to: "/faq", label: t("nav.faq") },
  ];

  const linkCls = "text-[13px] font-medium uppercase tracking-[0.18em] text-[#DCE2EA] hover:text-white transition-colors duration-300";

  return (
    <>
      <div
        data-testid="floating-logo"
        style={{
          position: "fixed",
          top: logoTop,
          left: "50%",
          transform: "translateX(-50%) translateZ(0)",
          WebkitTransform: "translateX(-50%) translateZ(0)",
          backfaceVisibility: "hidden",
          WebkitBackfaceVisibility: "hidden",
          width: logoW,
          zIndex: 60,
        }}
      >
        <Link
          to="/"
          data-testid="nav-logo"
          aria-label="ROMACASA Home"
          onClick={(e) => {
            if (isHome) {
              e.preventDefault();
              window.scrollTo({ top: 0, behavior: "smooth" });
            }
          }}
        >
          <img src="/logo-white.png" alt="ROMACASA Intermediazioni Immobiliari" className="w-full h-auto drop-shadow-[0_2px_14px_rgba(0,0,0,0.4)]" />
        </Link>
      </div>

      <header
        data-testid="site-nav"
        className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
          scrolled || !isHome ? "bg-[#000000]/95 backdrop-blur-xl shadow-[0_12px_40px_-18px_rgba(16,21,27,0.5)]" : "bg-gradient-to-b from-[#000000]/70 to-transparent"
        }`}
      >
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <div className="relative flex items-center justify-center py-3.5">
            <div className="h-9 sm:h-11" aria-hidden="true" />
            <div className="absolute right-0 top-1/2 -translate-y-1/2 flex items-center gap-4">
              <div className="hidden sm:block"><LangSwitch /></div>
              <a href={AGENCY.phoneHref} data-testid="nav-phone-button" className="hidden xl:flex items-center gap-2 text-[#DCE2EA] hover:text-white text-sm transition-colors">
                <Phone size={14} /> {AGENCY.phone}
              </a>
              <button data-testid="nav-menu-toggle" onClick={() => setOpen(!open)} className="lg:hidden p-1 text-[#DCE2EA]" aria-label="Menu">
                {open ? <X size={26} /> : <Menu size={26} />}
              </button>
            </div>
          </div>

          <nav className="lg:hidden flex items-center gap-6 overflow-x-auto whitespace-nowrap pb-3 -mx-5 px-5 scrollbar-none" data-testid="mobile-menu-strip">
            <NavLink to="/" data-testid="strip-link-home" className="text-[11px] font-medium uppercase tracking-[0.18em] text-[#DCE2EA]">{t("nav.home")}</NavLink>
            <NavLink to="/immobili" data-testid="strip-link-immobili" className="text-[11px] font-medium uppercase tracking-[0.18em] text-[#DCE2EA]">{t("nav.immobili")}</NavLink>
            <NavLink to="/immobili?featured=true" data-testid="strip-link-evidenza" className="text-[11px] font-medium uppercase tracking-[0.18em] text-[#DCE2EA]">{t("nav.evidenza")}</NavLink>
            <NavLink to="/mappa" data-testid="strip-link-mappa" className="text-[11px] font-medium uppercase tracking-[0.18em] text-[#DCE2EA]">{t("nav.mappa")}</NavLink>
            <NavLink to="/chi-siamo" data-testid="strip-link-chi-siamo" className="text-[11px] font-medium uppercase tracking-[0.18em] text-[#DCE2EA]">{t("nav.chiSiamo")}</NavLink>
            <NavLink to="/blog" data-testid="strip-link-blog" className="text-[11px] font-medium uppercase tracking-[0.18em] text-[#DCE2EA]">{t("nav.blog")}</NavLink>
            <NavLink to="/faq" data-testid="strip-link-faq" className="text-[11px] font-medium uppercase tracking-[0.18em] text-[#DCE2EA]">{t("nav.faq")}</NavLink>
            <NavLink to="/contatti" data-testid="strip-link-contatti" className="text-[11px] font-medium uppercase tracking-[0.18em] text-[#DCE2EA]">{t("nav.contatti")}</NavLink>
          </nav>

          <nav className="hidden lg:flex items-center justify-center gap-9 pb-3.5" data-testid="desktop-menu">
            <NavLink to="/" data-testid="nav-link-home" className={linkCls}>{t("nav.home")}</NavLink>
            <div onMouseEnter={openDrop("immobili")} onMouseLeave={closeDrop}>
              <NavLink to="/immobili" data-testid="nav-link-immobili" className={`${linkCls} flex items-center gap-1.5`}>
                {t("nav.immobili")} <ChevronDown size={13} className={`opacity-70 transition-transform duration-300 ${drop === "immobili" ? "rotate-180" : ""}`} />
              </NavLink>
            </div>
            <NavLink to="/immobili?featured=true" data-testid="nav-link-evidenza" className={linkCls}>{t("nav.evidenza")}</NavLink>
            <NavLink to="/mappa" data-testid="nav-link-mappa" className={linkCls}>{t("nav.mappa")}</NavLink>
            <div onMouseEnter={openDrop("agenzia")} onMouseLeave={closeDrop}>
              <NavLink to="/chi-siamo" data-testid="nav-link-chi-siamo" className={`${linkCls} flex items-center gap-1.5`}>
                {t("nav.chiSiamo")} <ChevronDown size={13} className={`opacity-70 transition-transform duration-300 ${drop === "agenzia" ? "rotate-180" : ""}`} />
              </NavLink>
            </div>
            <NavLink to="/contatti" data-testid="nav-link-contatti" className={linkCls}>{t("nav.contatti")}</NavLink>
          </nav>
        </div>
      </header>

      {drop &&
        createPortal(
          <div
            data-testid={`dropdown-${drop}`}
            onMouseEnter={() => {
              if (closeTimer.current) clearTimeout(closeTimer.current);
              setDrop(drop);
            }}
            onMouseLeave={closeDrop}
            style={{ position: "fixed", top: dropPos.top, left: dropPos.left, transform: "translateX(-50%)", zIndex: 80 }}
          >
            <div className="pt-3">
              <div className="bg-black border border-[#262626] min-w-[240px] py-2 shadow-[0_24px_60px_-20px_rgba(0,0,0,0.6)]">
                {(drop === "immobili" ? immobiliMenu : agenziaMenu).map((m) => (
                  <NavLink
                    key={m.key || m.to.split("=")[1]}
                    to={m.to}
                    data-testid={`nav-sub-${m.key || m.to.split("=")[1]}`}
                    onClick={() => setDrop(null)}
                    className="block px-6 py-2.5 text-[13px] tracking-wide text-[#DCE2EA] hover:text-white hover:bg-[#111111] transition-colors"
                  >
                    {m.label}
                  </NavLink>
                ))}
              </div>
            </div>
          </div>,
          document.body
        )}

      {open && (
        <div data-testid="mobile-menu" className="fixed inset-0 z-40 bg-[#000000] flex flex-col justify-center px-8 lg:hidden overflow-y-auto pt-24">
          <nav className="flex flex-col gap-5">
            <NavLink to="/" data-testid="mobile-nav-home" className="font-serif-d text-3xl text-[#FAFAF7]">{t("nav.home")}</NavLink>
            <NavLink to="/immobili" data-testid="mobile-nav-immobili" className="font-serif-d text-3xl text-[#FAFAF7]">{t("nav.immobili")}</NavLink>
            <div className="flex flex-wrap gap-x-5 gap-y-2 pl-4 border-l border-[#262626]">
              {immobiliMenu.map((m) => (
                <NavLink key={m.to} to={m.to} data-testid={`mobile-nav-${m.to.split("=")[1]}`} className="text-sm text-[#8A94A0] hover:text-[#E8E8E8]">{m.label}</NavLink>
              ))}
            </div>
            <NavLink to="/immobili?featured=true" data-testid="mobile-nav-evidenza" className="font-serif-d text-3xl text-[#FAFAF7]">{t("nav.evidenza")}</NavLink>
            <NavLink to="/mappa" data-testid="mobile-nav-mappa" className="font-serif-d text-3xl text-[#FAFAF7]">{t("nav.mappa")}</NavLink>
            <NavLink to="/chi-siamo" data-testid="mobile-nav-chi-siamo" className="font-serif-d text-3xl text-[#FAFAF7]">{t("nav.chiSiamo")}</NavLink>
            <NavLink to="/blog" data-testid="mobile-nav-blog" className="font-serif-d text-3xl text-[#FAFAF7]">{t("nav.blog")}</NavLink>
            <NavLink to="/faq" data-testid="mobile-nav-faq" className="font-serif-d text-3xl text-[#FAFAF7]">{t("nav.faq")}</NavLink>
            <NavLink to="/contatti" data-testid="mobile-nav-contatti" className="font-serif-d text-3xl text-[#FAFAF7]">{t("nav.contatti")}</NavLink>
            <div className="mt-4 flex items-center justify-between">
              <a href={AGENCY.phoneHref} data-testid="mobile-nav-phone" className="flex items-center gap-3 text-[#E8E8E8] text-lg"><Phone size={18} /> {AGENCY.phone}</a>
              <LangSwitch />
            </div>
          </nav>
        </div>
      )}
    </>
  );
};
